import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UserService } from 'src/app/core/services/user.service';

// CPK-4585 (CPK-3824 Fluxo 1): alternate recovery entry point for a client
// who doesn't know/remember the exact email their account is under. Asks for
// CPF + email + phone instead — the backend resolves the right account and
// sends the code wherever it's actually registered.
@Component({
  selector: 'app-recovery-cpf',
  templateUrl: './recovery-cpf.component.html',
  styleUrls: ['./recovery-cpf.component.scss'],
})
export class RecoveryCpfComponent implements OnInit {
  @Input() title: string = 'ForgotPassword';
  isLoading = false;
  captchaToken: string | null = null;
  // TEMP (CPK-4685): when true, the real Turnstile widget is never mounted —
  // see queryParams handling below for why.
  captchaBypassed = false;
  // Same default as the phone-login screen (login.component.ts) — Brazil first.
  preferredCountries = ['br', 'us', 'gb'];

  lastSendTime: number | null = null;
  sendCodePermission = false;

  form: FormGroup<any> = new FormGroup({});
  @Output() onSubmit: EventEmitter<{ cpf: string; email: string; phone: string }> =
    new EventEmitter();

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private actRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.form = this.fb.group({
      cpf: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required]],
    });

    // TEMP (CPK-4685): lets manual testing get past the Turnstile widget
    // while the site key's domain allowlist in Cloudflare doesn't include
    // this environment yet. Pass the real TURNSTILE_BYPASS/AGENT_BYPASS
    // value for the stage as ?bypassCaptcha=<token> — never hardcoded here.
    // The widget itself is skipped (see .html) when bypassed: since the site
    // key is unauthorized in every env right now, mounting it would fire
    // Turnstile's error-callback and wipe this token right back out.
    // REMOVE once the Cloudflare domain allowlist is fixed.
    this.actRoute.queryParams.subscribe((params) => {
      if (params['bypassCaptcha']) {
        this.captchaToken = params['bypassCaptcha'];
        this.captchaBypassed = true;
      }
    });

    setInterval(() => {
      const now = new Date().getTime();
      if (!this.lastSendTime) {
        this.sendCodePermission = true;
        return;
      }
      this.sendCodePermission = now - this.lastSendTime > 1000 * 60;
    }, 1000);
  }

  getDiffLastTimeAndNowInSec() {
    if (!this.lastSendTime) return 0;
    return 60 - Math.floor((new Date().getTime() - this.lastSendTime) / 1000);
  }

  async submit() {
    try {
      this.isLoading = true;

      if (!this.captchaToken) throw new Error('Captcha is required');
      const { cpf, email, phone } = this.form.value;

      await this.userService.requestAccountRecoveryByCpf(cpf, email, phone, this.captchaToken);

      this.lastSendTime = new Date().getTime();
      this.sendCodePermission = false;
      this.onSubmit.emit({ cpf, email, phone });
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }
}
